import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, XCircle, Clock, FileText, Send, PlayCircle, StopCircle, AlertTriangle, FileCheck } from 'lucide-react';

const PTWTimeline = ({ permit }) => {
  const getTimelineEvents = () => {
    const events = [];
    
    // Created event
    if (permit.created_at) {
      events.push({
        title: 'Permit Created',
        description: `Created as ${permit.permit_type} permit`,
        timestamp: permit.created_at,
        icon: <FileText className="w-5 h-5" />,
        color: 'bg-gray-500',
        user: permit.applicant_name || 'System'
      });
    }
    
    // Status transitions
    if (permit.status === 'Pending Approval' || permit.status === 'Approved' || permit.status === 'Rejected' || permit.status === 'Active' || permit.status === 'Closed' || permit.status === 'HSE Verified') {
      events.push({
        title: 'Submitted for Approval',
        description: 'Permit submitted for review',
        timestamp: permit.updated_at,
        icon: <Send className="w-5 h-5" />,
        color: 'bg-blue-500',
        user: permit.applicant_name
      });
    }
    
    // Approvals
    if (permit.approvals && permit.approvals.length > 0) {
      permit.approvals.forEach((approval, index) => {
        if (approval.approval_status === 'Approved' && approval.approval_date) {
          events.push({
            title: `Approved by ${approval.approver_role}`,
            description: approval.comments || 'Permit approved',
            timestamp: approval.approval_date,
            icon: <CheckCircle className="w-5 h-5" />,
            color: 'bg-green-500',
            user: approval.approver_name
          });
        } else if (approval.approval_status === 'Rejected' && approval.approval_date) {
          events.push({
            title: `Rejected by ${approval.approver_role}`,
            description: approval.comments || 'Permit rejected',
            timestamp: approval.approval_date,
            icon: <XCircle className="w-5 h-5" />,
            color: 'bg-red-500',
            user: approval.approver_name
          });
        }
      });
    }
    
    // Issued/Activated
    if (permit.permit_issued_date) {
      events.push({
        title: 'Permit Issued & Activated',
        description: 'Permit is now active for work',
        timestamp: permit.permit_issued_date,
        icon: <PlayCircle className="w-5 h-5" />,
        color: 'bg-green-600',
        user: permit.permit_issued_by_name || 'System'
      });
    }
    
    // Extension requested
    if (permit.extension_requested && permit.extension_reason) {
      events.push({
        title: 'Extension Requested',
        description: permit.extension_reason,
        timestamp: permit.updated_at,
        icon: <Clock className="w-5 h-5" />,
        color: 'bg-orange-500',
        user: permit.applicant_name
      });
    }
    
    // Extension approved
    if (permit.extension_approved_date) {
      events.push({
        title: 'Extension Approved',
        description: `Extended until ${new Date(permit.extension_end_date).toLocaleString()}`,
        timestamp: permit.extension_approved_date,
        icon: <CheckCircle className="w-5 h-5" />,
        color: 'bg-orange-600',
        user: permit.extension_approved_by || 'System'
      });
    }
    
    // Suspended
    if (permit.is_suspended && permit.suspended_date) {
      events.push({
        title: 'Permit Suspended',
        description: permit.suspended_reason || 'Emergency suspension',
        timestamp: permit.suspended_date,
        icon: <StopCircle className="w-5 h-5" />,
        color: 'bg-red-600',
        user: 'HSE Manager'
      });
    }
    
    // Closed
    if (permit.work_completed_date) {
      events.push({
        title: 'Permit Closed',
        description: permit.closure_remarks || 'Work completed',
        timestamp: permit.work_completed_date,
        icon: <FileCheck className="w-5 h-5" />,
        color: 'bg-purple-600',
        user: permit.closed_by_name || 'System'
      });
    }
    
    // HSE Verified
    if (permit.hse_verified && permit.hse_verified_date) {
      events.push({
        title: 'HSE Verification Complete',
        description: permit.hse_verification_remarks || 'Final verification completed',
        timestamp: permit.hse_verified_date,
        icon: <CheckCircle className="w-5 h-5" />,
        color: 'bg-green-700',
        user: permit.hse_verified_by_name || 'HSE Manager'
      });
    }
    
    // Expired
    if (permit.status === 'Expired') {
      events.push({
        title: 'Permit Expired',
        description: 'Permit validity period ended',
        timestamp: permit.work_end_date,
        icon: <AlertTriangle className="w-5 h-5" />,
        color: 'bg-gray-600',
        user: 'System'
      });
    }
    
    // Sort by timestamp (newest first)
    return events.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  };

  const events = getTimelineEvents();

  if (events.length === 0) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="w-5 h-5" />
          Status Timeline & Audit Trail
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gray-200" />
          
          {/* Timeline events */}
          <div className="space-y-6">
            {events.map((event, index) => (
              <div key={index} className="relative flex gap-4 items-start">
                {/* Icon */}
                <div className={`relative z-10 flex items-center justify-center w-12 h-12 rounded-full ${event.color} text-white shadow-lg`}>
                  {event.icon}
                </div>
                
                {/* Content */}
                <div className="flex-1 pb-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="text-sm font-bold text-gray-900">{event.title}</h4>
                      <p className="text-sm text-gray-600 mt-1">{event.description}</p>
                      <p className="text-xs text-gray-500 mt-2">
                        <span className="font-semibold">{event.user}</span>
                        {' • '}
                        {new Date(event.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PTWTimeline;
